import React, { useEffect, useState } from "react";
import { AlertCircle, BadgeCheck, ChevronLeft, ChevronRight, Download, ImageOff, X } from "lucide-react";
import {
  getBulandiEventRulesPngDownloadFilename,
  getBulandiEventRulesPngUrls,
} from "../../utils/bulandiEventRulesPng";
import {
  getBulandiEventWinnersPngDownloadFilename,
  getBulandiEventWinnersPngUrls,
  hasBulandiEventWinnersPng,
} from "../../utils/bulandiEventWinnersPng";
import { BULANDI_OFFICIAL_WHATSAPP_GROUP_URL } from "../../config/whatsappMessaging";

function RegistrationSuccessFollowUp({ whatsappGroupUrl = BULANDI_OFFICIAL_WHATSAPP_GROUP_URL }) {
  const groupUrl = String(whatsappGroupUrl ?? "").trim();
  return (
    <>
      <p className="mt-3 text-sm text-bul-mutedfg">
        Please check your email for confirmation details.
      </p>
      {groupUrl ? (
        <p className="mt-3 text-sm">
          <a
            href={groupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-bul-lime underline underline-offset-2 hover:text-bul-lime/90"
          >
            Join the WhatsApp group
          </a>
        </p>
      ) : null}
    </>
  );
}
export function WorkshopDetailsModal({ workshop, onClose }) {
  const src = workshop?.detailsImageUrl?.trim();
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    setImgFailed(false);
  }, [workshop?.id, src]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  if (!workshop) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bulandi-modal-backdrop-strong backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={workshop.title}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute right-4 top-4 z-[60] grid h-11 w-11 place-items-center rounded-full border-2 border-bul-border bg-bul-surface text-bul-mutedfg shadow-bul-brutal-sm transition hover:border-bul-lime/50 hover:text-bul-lime sm:right-6 sm:top-6 sm:h-12 sm:w-12"
        aria-label="Close"
      >
        <X className="h-5 w-5" strokeWidth={2.5} aria-hidden />
      </button>
      <div
        className="flex min-h-0 flex-1 items-center justify-center px-4 pt-14 sm:px-8 sm:pt-16"
        onClick={onClose}
        role="presentation"
      >
        {src && !imgFailed ? (
          <img
            src={src}
            alt={workshop.title}
            className="max-h-[min(82dvh,100%)] max-w-[min(96vw,100%)] object-contain"
            loading="eager"
            decoding="async"
            onClick={(e) => e.stopPropagation()}
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div
            className="flex max-w-md flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-bul-border bg-bul-surface/60 px-8 py-12 text-bul-mutedfg"
            onClick={(e) => e.stopPropagation()}
            role="note"
          >
            <ImageOff className="h-14 w-14 opacity-50" aria-hidden />
            <p className="text-center text-sm">
              Workshop details image is not available yet.
            </p>
          </div>
        )}
      </div>
      <p className="shrink-0 px-4 pb-6 pt-2 text-center font-display text-[0.84375rem] uppercase tracking-tight text-bul-fg sm:pb-8 sm:text-[1rem]">
        {workshop.title}
      </p>
    </div>
  );
}
export function WorkshopRegistrationSuccessModal({ onClose, whatsappGroupUrl = "" }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bulandi-modal-backdrop-soft p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border-2 border-bul-border bg-bul-surface shadow-bul-brutal-sm"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="workshop-reg-success-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-bul-border bg-bul-bg/60 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-bul-lime">
              Bulandi 2026
            </p>
            <h2
              id="workshop-reg-success-title"
              className="mt-1 pr-2 font-display text-[0.84375rem] uppercase tracking-tight text-bul-fg"
            >
              Workshop registration
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-2 text-bul-mutedfg transition hover:bg-bul-border/60 hover:text-bul-lime"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>
        <div className="p-6 text-center sm:p-7">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-bul-lime/15 ring-4 ring-bul-lime/25"
            aria-hidden
          >
            <BadgeCheck className="h-8 w-8 text-bul-lime" strokeWidth={2.25} />
          </div>
          <p className="text-sm leading-relaxed text-bul-mutedfg">
            Thank you — your workshop registration was received.
          </p>
          <RegistrationSuccessFollowUp whatsappGroupUrl={whatsappGroupUrl} />
          <button
            type="button"
            onClick={onClose}
            className="bul-cta-primary mt-6 w-full py-3 text-base"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
function isPdfScheduleUrl(url) {
  return /\.pdf(?:[#?].*)?$/i.test(String(url ?? "").trim());
}

export function BulandiScheduleModal({
  imageUrl,
  pdfUrl,
  downloadFilename,
  onClose,
}) {
  const src = (pdfUrl || imageUrl)?.trim();
  const isPdf = isPdfScheduleUrl(src);
  const downloadName =
    String(
      downloadFilename ||
        (isPdf ? "Bulandi-2026-schedule.pdf" : "Bulandi-2026-schedule.png"),
    ).trim() ||
    (isPdf ? "Bulandi-2026-schedule.pdf" : "Bulandi-2026-schedule.png");
  const previewSrc = isPdf && src ? `${src}#view=FitH&toolbar=0` : src;
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [src]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  if (!src) return null;
  return (
    <div
      className={`fixed inset-0 z-50 bulandi-modal-backdrop-strong backdrop-blur-sm${
        isPdf
          ? " flex max-w-[100vw] flex-col overflow-x-hidden overflow-y-hidden"
          : ""
      }`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Bulandi schedule"
    >
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-end gap-2 p-3 sm:p-6">
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="grid h-11 w-11 place-items-center rounded-full border-2 border-bul-border bg-bul-surface text-bul-mutedfg shadow-bul-brutal-sm transition hover:border-bul-lime/50 hover:text-bul-lime sm:h-12 sm:w-12"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2.5} aria-hidden />
          </button>
          <a
            href={src}
            download={downloadName}
            onClick={(e) => e.stopPropagation()}
            className="grid h-11 w-11 place-items-center rounded-full border-2 border-[color:var(--bul-strong-outline)] bg-gradient-to-br from-[#eeff8a] to-bul-lime text-[rgb(var(--bul-primary-cta-text-rgb))] shadow-bul-brutal-sm transition hover:brightness-105 sm:h-12 sm:w-12"
            aria-label={isPdf ? "Download schedule PDF" : "Download schedule"}
          >
            <Download className="h-5 w-5" strokeWidth={2.5} aria-hidden />
          </a>
        </div>
      </div>

      {isPdf ? (
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-3 pt-14 sm:px-6 sm:pb-4 sm:pt-20"
          onClick={onClose}
          role="presentation"
        >
          <div
            className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col overflow-x-hidden overflow-y-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={previewSrc}
              title="Bulandi 2026 schedule"
              className="min-h-0 w-full min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto rounded-xl border-2 border-bul-border bg-bul-surface shadow-bul-brutal-sm"
            />
          </div>
          <p className="mt-2 shrink-0 text-center font-mono text-[10px] font-bold uppercase tracking-wide text-bul-mutedfg sm:mt-3 sm:text-xs">
            Bulandi 2026 Schedule
          </p>
        </div>
      ) : (
        <div
          className="flex h-full w-full items-center justify-center px-4 pb-6 pt-[4.5rem] sm:px-8 sm:pt-24"
          onClick={onClose}
          role="presentation"
        >
          {!imageFailed ? (
            <img
              src={src}
              alt="Bulandi 2026 schedule"
              className="max-h-[min(100dvh-7rem,100%)] max-w-full object-contain"
              loading="eager"
              decoding="async"
              onClick={(e) => e.stopPropagation()}
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div
              className="flex max-w-md flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-bul-border bg-bul-surface/60 px-8 py-12 text-bul-mutedfg"
              onClick={(e) => e.stopPropagation()}
              role="note"
            >
              <ImageOff className="h-14 w-14 opacity-50" aria-hidden />
              <p className="text-center text-sm">
                Schedule image is not available yet.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
export function BulandiRulebookPdfModal({ pdfUrl, downloadFilename, onClose }) {
  const src = pdfUrl?.trim();
  const downloadName =
    String(downloadFilename || "Bulandi-2026-Event-rule-book.pdf").trim() ||
    "Bulandi-2026-Event-rule-book.pdf";
  const previewSrc = src ? `${src}#view=FitH&toolbar=0` : "";
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex max-w-[100vw] flex-col overflow-x-hidden overflow-y-hidden bulandi-modal-backdrop-strong backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Bulandi 2026 event rulebook"
    >
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-end gap-2 p-3 sm:p-6">
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="grid h-11 w-11 place-items-center rounded-full border-2 border-bul-border bg-bul-surface text-bul-mutedfg shadow-bul-brutal-sm transition hover:border-bul-lime/50 hover:text-bul-lime sm:h-12 sm:w-12"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2.5} aria-hidden />
          </button>
          <a
            href={src}
            download={downloadName}
            onClick={(e) => e.stopPropagation()}
            className="grid h-11 w-11 place-items-center rounded-full border-2 border-[color:var(--bul-strong-outline)] bg-gradient-to-br from-[#eeff8a] to-bul-lime text-[rgb(var(--bul-primary-cta-text-rgb))] shadow-bul-brutal-sm transition hover:brightness-105 sm:h-12 sm:w-12"
            aria-label="Download rulebook PDF"
          >
            <Download className="h-5 w-5" strokeWidth={2.5} aria-hidden />
          </a>
        </div>
      </div>

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-3 pt-14 sm:px-6 sm:pb-4 sm:pt-20"
        onClick={onClose}
        role="presentation"
      >
        <div
          className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col overflow-x-hidden overflow-y-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            src={previewSrc}
            title="Bulandi 2026 event rulebook"
            className="min-h-0 w-full min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto rounded-xl border-2 border-bul-border bg-bul-surface shadow-bul-brutal-sm"
          />
        </div>
        <p className="mt-2 shrink-0 text-center font-mono text-[10px] font-bold uppercase tracking-wide text-bul-mutedfg sm:mt-3 sm:text-xs">
          Bulandi 2026 Event Rulebook
        </p>
      </div>
    </div>
  );
}
export function RulesModal({ event, onClose }) {
  const isWinners = hasBulandiEventWinnersPng(event);
  const pngUrls = isWinners
    ? (getBulandiEventWinnersPngUrls(event) ?? [])
    : (getBulandiEventRulesPngUrls(event) ?? []);
  const [pageIndex, setPageIndex] = useState(0);
  const [rulesImageFailed, setRulesImageFailed] = useState(false);
  const rulesPngUrl = pngUrls[pageIndex] ?? null;
  const rulesPngDownloadName = isWinners
    ? getBulandiEventWinnersPngDownloadFilename(event, pageIndex)
    : getBulandiEventRulesPngDownloadFilename(event, pageIndex);
  const hasMultiplePages = pngUrls.length > 1;
  const assetLabel = isWinners ? "winners" : "rules";
  useEffect(() => {
    setPageIndex(0);
    setRulesImageFailed(false);
  }, [event?.id]);
  useEffect(() => {
    setRulesImageFailed(false);
  }, [pageIndex, rulesPngUrl]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasMultiplePages) {
        setPageIndex((prev) => (prev > 0 ? prev - 1 : pngUrls.length - 1));
      }
      if (e.key === "ArrowRight" && hasMultiplePages) {
        setPageIndex((prev) => (prev < pngUrls.length - 1 ? prev + 1 : 0));
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, hasMultiplePages, pngUrls.length]);
  const goPrev = (e) => {
    e.stopPropagation();
    setPageIndex((prev) => (prev > 0 ? prev - 1 : pngUrls.length - 1));
  };
  const goNext = (e) => {
    e.stopPropagation();
    setPageIndex((prev) => (prev < pngUrls.length - 1 ? prev + 1 : 0));
  };
  return (
    <div
      className="fixed inset-0 z-50 bulandi-modal-backdrop-strong backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isWinners ? "Event winners" : "Event rules"}
    >
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-end gap-2 p-4 sm:p-6">
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="grid h-11 w-11 place-items-center rounded-full border-2 border-bul-border bg-bul-surface text-bul-mutedfg shadow-bul-brutal-sm transition hover:border-bul-lime/50 hover:text-bul-lime sm:h-12 sm:w-12"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2.5} aria-hidden />
          </button>
          {rulesPngUrl ? (
            <a
              href={rulesPngUrl}
              download={rulesPngDownloadName}
              onClick={(e) => e.stopPropagation()}
              className="grid h-11 w-11 place-items-center rounded-full border-2 border-[color:var(--bul-strong-outline)] bg-gradient-to-br from-[#eeff8a] to-bul-lime text-[rgb(var(--bul-primary-cta-text-rgb))] shadow-bul-brutal-sm transition hover:brightness-105 sm:h-12 sm:w-12"
              aria-label={`Download ${assetLabel} as PNG`}
            >
              <Download className="h-5 w-5" strokeWidth={2.5} aria-hidden />
            </a>
          ) : null}
        </div>
      </div>

      <div
        className="relative flex h-full w-full items-center justify-center px-4 pb-6 pt-[4.5rem] sm:px-8 sm:pt-24"
        onClick={onClose}
        role="presentation"
      >
        {hasMultiplePages ? (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-3 top-1/2 z-[55] grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border-2 border-bul-border bg-bul-surface text-bul-mutedfg shadow-bul-brutal-sm transition hover:border-bul-lime/50 hover:text-bul-lime sm:left-6 sm:h-12 sm:w-12"
            aria-label={`Previous ${assetLabel} page`}
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2.5} aria-hidden />
          </button>
        ) : null}
        {rulesPngUrl && !rulesImageFailed ? (
          <img
            src={rulesPngUrl}
            alt={`${event?.name || "Event"} ${assetLabel}${hasMultiplePages ? ` — page ${pageIndex + 1}` : ""}`}
            className="max-h-[min(100dvh-7rem,100%)] max-w-full object-contain"
            loading="eager"
            decoding="async"
            onClick={(e) => e.stopPropagation()}
            onError={() => setRulesImageFailed(true)}
          />
        ) : (
          <div
            className="flex max-w-md flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-bul-border bg-bul-surface/60 px-8 py-12 text-bul-mutedfg"
            onClick={(e) => e.stopPropagation()}
            role="note"
          >
            <ImageOff className="h-14 w-14 opacity-50" aria-hidden />
            <p className="text-center text-sm">
              {isWinners ? "Winners image is not available yet." : "Rules image is not available yet."}
            </p>
          </div>
        )}
        {hasMultiplePages ? (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-3 top-1/2 z-[55] grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border-2 border-bul-border bg-bul-surface text-bul-mutedfg shadow-bul-brutal-sm transition hover:border-bul-lime/50 hover:text-bul-lime sm:right-6 sm:h-12 sm:w-12"
            aria-label={`Next ${assetLabel} page`}
          >
            <ChevronRight className="h-5 w-5" strokeWidth={2.5} aria-hidden />
          </button>
        ) : null}
      </div>
      {hasMultiplePages ? (
        <p className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] text-center font-mono text-xs font-bold uppercase tracking-wide text-bul-mutedfg">
          Page {pageIndex + 1} of {pngUrls.length}
        </p>
      ) : null}
      {event?.name && hasMultiplePages ? (
        <p className="pointer-events-none fixed inset-x-0 bottom-10 z-[60] text-center font-display text-[0.84375rem] uppercase tracking-tight text-bul-fg sm:bottom-12 sm:text-[1rem]">
          {event.name}
        </p>
      ) : null}
    </div>
  );
}
export function BrConfirmationModal({ brNumber, onClose, onGoToEventRegistration }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bulandi-modal-backdrop-soft p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl border-2 border-bul-border bg-bul-surface p-6 shadow-bul-brutal-sm sm:p-7"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="br-modal-title"
        aria-describedby="br-modal-desc"
      >
        <p className="mb-1 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-bul-lime">
          Bulandi 2026
        </p>
        <h2
          id="br-modal-title"
          className="font-display text-[0.9375rem] uppercase tracking-tight text-bul-fg"
        >
          Registration received
        </h2>
        <p id="br-modal-desc" className="mt-3 text-sm text-bul-mutedfg">
          Save your Bulandi registration number for your records and for event
          coordination.
        </p>
        <RegistrationSuccessFollowUp />
        <p className="mt-4 rounded-xl border-2 border-dashed border-bul-lime/50 bg-bul-lime/10 px-4 py-3 text-center font-mono text-lg font-black tracking-wide text-bul-lime">
          {brNumber}
        </p>
        {typeof onGoToEventRegistration === "function" ? (
          <button
            type="button"
            onClick={onGoToEventRegistration}
            className="bul-cta-primary mt-6 w-full py-3 text-base"
          >
            Register for competitions
          </button>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          className={
            typeof onGoToEventRegistration === "function"
              ? "mt-3 w-full rounded-xl border-2 border-bul-border bg-transparent py-3 text-sm font-mono font-bold uppercase tracking-wider text-bul-mutedfg transition hover:border-bul-lime/50 hover:text-bul-lime"
              : "bul-cta-primary mt-6 w-full py-3 text-base"
          }
        >
          {typeof onGoToEventRegistration === "function"
            ? "Browse event catalogue"
            : "OK"}
        </button>
      </div>
    </div>
  );
}
export function EventRegistrationSuccessModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bulandi-modal-backdrop-soft p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border-2 border-bul-border bg-bul-surface shadow-bul-brutal-sm"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-reg-success-title"
        aria-describedby="event-reg-success-desc"
      >
        <div className="flex items-start justify-between gap-3 border-b border-bul-border bg-bul-bg/60 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-bul-lime">
              Bulandi 2026
            </p>
            <h2
              id="event-reg-success-title"
              className="mt-1 pr-2 font-display text-[0.84375rem] uppercase tracking-tight text-bul-fg"
            >
              Thank you
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-2 text-bul-mutedfg transition hover:bg-bul-border/60 hover:text-bul-lime"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>
        <div className="p-6 text-center sm:p-7">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-bul-lime/15 ring-4 ring-bul-lime/25"
            aria-hidden
          >
            <BadgeCheck className="h-8 w-8 text-bul-lime" strokeWidth={2.25} />
          </div>
          <p
            id="event-reg-success-desc"
            className="text-sm leading-relaxed text-bul-mutedfg"
          >
            Your competition choices have been saved successfully.
          </p>
          <RegistrationSuccessFollowUp />
          <button
            type="button"
            onClick={onClose}
            className="bul-cta-primary mt-6 w-full py-3 text-base"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
export function EventRegistrationErrorModal({ message, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  if (!message) return null;
  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center bulandi-modal-backdrop-soft p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border-2 border-red-800/55 bg-bul-surface shadow-bul-brutal-sm"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="event-reg-err-title"
        aria-describedby="event-reg-err-desc"
      >
        <div className="flex items-start justify-between gap-3 border-b border-red-900/40 bg-red-950/40 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-red-300/90">
              Bulandi 2026
            </p>
            <h2
              id="event-reg-err-title"
              className="mt-1 pr-2 font-display text-[0.84375rem] uppercase tracking-tight text-bul-fg"
            >
              Could not complete
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-2 text-bul-mutedfg transition hover:bg-bul-border/60 hover:text-bul-lime"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>
        <div className="p-6 text-center sm:p-7">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-950/60 ring-4 ring-red-900/35"
            aria-hidden
          >
            <AlertCircle className="h-8 w-8 text-red-400" strokeWidth={2.25} />
          </div>
          <p
            id="event-reg-err-desc"
            className="text-left text-sm leading-relaxed text-red-100/95"
          >
            {message}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="bul-cta-primary mt-6 w-full py-3 text-base"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
